"use client";

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Menu, X, ChevronDown } from 'lucide-react';
import { buttonVariants } from '../ui/Button';
import { signOut } from '@/lib/signout';
import { classifySessionProbe, type SessionProbeOutcome } from '@/lib/session-probe';
import { initialAuthState, readSessionHint } from '@/lib/session-hint';
import { useTranslations } from '@/app/hooks/useTranslations';
import LanguageSelector from '@/app/components/LanguageSelector';

interface DropdownItem {
  href: string;
  label: string;
}

interface NavDropdown {
  label: string;
  items: DropdownItem[];
}

interface NavLink {
  href: string;
  label: string;
}

type NavItem = NavLink | NavDropdown;

function isDropdown(item: NavItem): item is NavDropdown {
  return 'items' in item;
}

const DesktopDropdown: React.FC<{ dropdown: NavDropdown }> = ({ dropdown }) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        className="flex items-center gap-1 text-gray-600 hover:text-primary-500 font-medium transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {dropdown.label}
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
          {dropdown.items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-primary-500 transition-colors"
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const { t } = useTranslations();
  const pathname = usePathname();

  /**
   * Only a definitive answer moves the header between signed-in and signed-out.
   * A 429 from the proxy's rate limiter, a 5xx, or an offline tab means the
   * server declined to answer — flipping to the signed-out header on those made
   * a signed-in user look logged out (and, on the dashboard, get bounced to
   * /login). On an indeterminate answer we keep whatever we last knew.
   */
  const checkAuth = async () => {
    let outcome: SessionProbeOutcome;

    try {
      const response = await fetch('/api/auth/check-session', {
        credentials: 'include',
        cache: 'no-store'
      });
      const body = await response.json().catch(() => null);
      outcome = classifySessionProbe(response, body);
    } catch {
      outcome = classifySessionProbe(null, null);
    }

    if (outcome.kind === 'indeterminate') {
      return;
    }

    const authenticated = outcome.kind === 'authenticated';
    const admin = outcome.kind === 'authenticated' && outcome.isAdmin;
    setIsAuthenticated(authenticated);
    setIsAdmin(admin);
    localStorage.setItem('isAuthenticated', String(authenticated));
    localStorage.setItem('isAdmin', String(admin));
  };

  /**
   * Paint the right link as soon as we hydrate, not when the probe answers.
   *
   * The server HTML has no auth state in it (the root layout is static on
   * purpose), and the probe took ~2.3s on a cold visit — so a signed-in user got
   * an auth-less navbar on their first page view and only saw "Dashboard" after
   * a reload, once localStorage had cached the answer. The proxy writes the
   * session hint on every document request, so the answer is already in the
   * cookie jar before any network call.
   *
   * The hint wins over localStorage: it is derived from the cookies sent with
   * *this* request, so it is right when the other tab signed out or the session
   * expired. localStorage only covers the case where no hint was written.
   */
  useEffect(() => {
    setMounted(true);
    const hint = readSessionHint(document.cookie);
    const cachedAuth = localStorage.getItem('isAuthenticated') === 'true';
    const cachedAdmin = localStorage.getItem('isAdmin') === 'true';
    if (initialAuthState(hint, cachedAuth)) {
      setIsAuthenticated(true);
      setIsAdmin(cachedAdmin);
    }
    checkAuth();
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setIsAuthenticated(false);
    setIsAdmin(false);
    try {
      // Shared with the dashboard top bar — see lib/signout.
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setMobileExpanded(null);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navItems: NavItem[] = [
    {
      label: t('footer.platform'),
      items: [
        { href: '/solutions', label: t('nav.solutions') },
        { href: '/how-it-works', label: t('nav.howItWorks') },
        { href: '/features', label: t('nav.features') },
      ],
    },
    { href: '/pricing', label: t('nav.pricing') },
    // FREE TOOLS vs RESOURCES — the split is by what the item asks of the
    // visitor, not by content type.
    //
    // SendVC has no free tier, so these five are the entire free surface of the
    // product and the only way to judge the database before subscribing. They
    // were reachable from the footer only (see the note in Footer.tsx), which
    // is a fraction of the clicks a header dropdown gets. "Free tools" is also
    // an offer in a way "Resources" is not — the label does acquisition work.
    //
    // Two of the five (match preview, free VC list) need a sign-in. They are
    // still free, so the label holds, but do not add a "no signup" promise to
    // it without checking those two gates first.
    //
    // Everything here is ALSO in the footer, so moving an item between these
    // two menus never costs it a crawlable link. Keep each list at ~5: past
    // that a dropdown stops being scannable, which is what this split fixed.
    {
      label: t('footer.freeTools'),
      items: [
        { href: '/match-preview', label: t('footer.matchPreview') },
        { href: '/tools/safe-calculator', label: t('footer.safeCalculator') },
        { href: '/tools/cold-email-generator', label: t('footer.coldEmailGenerator') },
        { href: '/pitch-deck-templates', label: t('footer.pitchDeckTemplatesShort') },
        { href: '/free-vc-list', label: t('footer.freeVcList') },
      ],
    },
    {
      label: t('footer.resources'),
      items: [
        { href: '/blog', label: t('nav.blog') },
        { href: '/compare', label: t('footer.compare') },
        { href: '/investors', label: t('nav.investors') },
        { href: '/faq', label: t('nav.faq') },
        { href: '/partners', label: t('nav.partners') },
      ],
    },
    // Company + Legal used to be footer-only. Everything the footer links is
    // now also in the nav (both menus render this one array), with two
    // deliberate exceptions: /#upload-section is the CTA button beside this
    // nav, and /llms.txt + /llms-full.txt are static crawler files rather than
    // pages — they stay footer-only. /reports/state-of-early-stage-vc-2026 is
    // excluded by request.
    //
    // /about moved from a top-level link into Company; it is still one click
    // away and the top-level row had no space for a sixth standalone item.
    // Company carries the legal pages too, rather than a sixth top-level
    // "Legal" menu. Measured at 1024px: six labels overlap the logo and push
    // the Upload Pitch CTA off the row. Legal links are low-traffic and
    // conventionally live under Company, so they ride along here — this is the
    // one dropdown allowed past ~5 items, ordered company-then-legal.
    {
      label: t('footer.company'),
      items: [
        { href: '/about', label: t('nav.about') },
        { href: '/contact', label: t('pages.contact') },
        { href: '/trust', label: t('pages.trust') },
        { href: '/press', label: t('pages.press') },
        { href: '/terms', label: t('pages.terms') },
        { href: '/privacy', label: t('pages.privacy') },
        { href: '/cookies', label: t('pages.cookies') },
        { href: '/imprint', label: t('pages.imprint') },
      ],
    },
  ];

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center" onClick={handleLogoClick}>
            {/* alt="" is deliberate: the adjacent "SendVC" text already names this
                link, so a non-empty alt would make it read "SendVC Logo SendVC". */}
            <Image
              src="/logo.svg"
              alt=""
              width={40}
              height={40}
              className="h-10 w-auto"
              priority
              loading="eager"
            />
            <span className="ml-2 text-xl font-bold text-gray-900">SendVC</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item, idx) =>
              isDropdown(item) ? (
                <DesktopDropdown key={idx} dropdown={item} />
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
                >
                  {item.label}
                </Link>
              )
            )}
            {mounted && isAuthenticated && (
              <>
                <Link
                  href="/dashboard"
                  className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
                >
                  Dashboard
                </Link>
                {isAdmin && (
                  <Link
                    href="/admin"
                    className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className="text-gray-600 hover:text-primary-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSigningOut ? 'Signing out...' : 'Sign Out'}
                </button>
              </>
            )}
            {mounted && !isAuthenticated && (
              <Link
                href="/login"
                className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
              >
                Login
              </Link>
            )}
          </nav>

          {/* Language Selector - Desktop */}
          <div className="hidden lg:flex items-center ml-4">
            <LanguageSelector />
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:block">
            <Link href="/#upload-section" className={buttonVariants({ size: 'sm' })}>
              {t('common.uploadPitch')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-600 hover:text-gray-900 focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div id="mobile-menu" role="navigation" className="lg:hidden bg-white border-t border-gray-100 shadow-sm">
          <div className="container py-4">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item, idx) =>
                isDropdown(item) ? (
                  <div key={idx}>
                    <button
                      className="flex items-center justify-between w-full text-gray-600 hover:text-primary-500 font-medium transition-colors py-2"
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${mobileExpanded === item.label ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                    {mobileExpanded === item.label && (
                      <div className="pl-4 space-y-1 border-l-2 border-gray-100 ml-2 mb-2">
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.href}
                            href={subItem.href}
                            className="block text-gray-500 hover:text-primary-500 font-medium transition-colors py-1.5 text-sm"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="text-gray-600 hover:text-primary-500 font-medium transition-colors py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              )}
              {mounted && isAuthenticated && (
                <>
                  <Link
                    href="/dashboard"
                    className="text-gray-600 hover:text-primary-500 font-medium transition-colors py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {isAdmin && (
                    <Link
                      href="/admin"
                      className="text-blue-600 hover:text-blue-700 font-medium transition-colors py-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleSignOut();
                    }}
                    disabled={isSigningOut}
                    className="text-gray-600 hover:text-primary-500 font-medium transition-colors py-2 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSigningOut ? 'Signing out...' : 'Sign Out'}
                  </button>
                </>
              )}
              {mounted && !isAuthenticated && (
                <Link
                  href="/login"
                  className="text-gray-600 hover:text-primary-500 font-medium transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
              )}
              <div className="flex justify-center pt-2">
                <LanguageSelector />
              </div>
              <Link
                href="/#upload-section"
                className="bg-primary-500 text-white py-3 px-4 rounded-md font-medium hover:bg-primary-600 transition-colors text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('common.uploadPitch')}
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export { Header };
